import { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { IoMdCopy } from 'react-icons/io';
import { toast } from 'react-toastify';
import { Tooltip } from '@nextui-org/react';

import { cn } from '@/lib/utils';
import { MessageRole } from '@/types/socket';
import { useChat } from '@/contexts/chat-context';
import iconTivy from '/images/icon-tivy.svg';

interface ChatMessageProps {
  role: string;
  content: string;
  isLastMessage: boolean;
}

interface PromptButtonProps {
  prompt: string;
  isLastMessage: boolean;
  promptSelected: string | null;
  onPromptClick: (prompt: string) => void;
}

interface CopyableBlockProps {
  text: string;
  children: React.ReactNode;
}

interface MessageRendererProps {
  content: string;
  isLastMessage: boolean;
  promptSelected: string | null;
  onPromptClick: (prompt: string) => void;
}

const AssistantAvatar = () => (
  <div className="bg-[#FF6840] w-8 h-8 rounded-full flex items-center justify-center shrink-0">
    <img src={iconTivy} alt="Tivy" />
  </div>
);

const PromptButton = ({ prompt, isLastMessage, promptSelected, onPromptClick }: PromptButtonProps) => (
  <button
    disabled={!!promptSelected || !isLastMessage}
    onClick={() => onPromptClick(prompt)}
    className={cn(
      'inline-flex text-left px-3 py-2 my-3 mr-2 rounded-full border border-brandSecondaryBlue bg-white text-brandSecondaryBlue hover:enabled:bg-brandSecondaryBlue hover:enabled:text-white cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed',
      { '!bg-brandSecondaryBlue !text-white': promptSelected === prompt }
    )}
  >
    {prompt}
  </button>
);

const CopyableBlock = ({ text, children }: CopyableBlockProps) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  return (
    <div className="group bg-white rounded-lg border border-[#f2f2f2] p-2 px-4 relative pr-[100px] my-2">
      <Tooltip content="Copy message" color="foreground">
        <button
          onClick={handleCopy}
          className={cn(
            'absolute top-2 right-2 flex items-center gap-1 px-2 py-1.5 text-sm rounded-md bg-brandSecondaryBlue text-white hover:bg-brandSecondaryBlue/90 opacity-50 group-hover:opacity-100 transition-opacity',
            { '!bg-buttonGreen opacity-100': isCopied }
          )}
        >
          <IoMdCopy size={16} />
          {isCopied ? 'Copied' : 'Copy'}
        </button>
      </Tooltip>
      {children}
    </div>
  );
};

const MessageRenderer = ({ content, isLastMessage, promptSelected, onPromptClick }: MessageRendererProps) => {
  // Hide incomplete tags but keep the rest of the content
  const cleanContent = content.replace(/<[^>]+/g, '');

  const components = useMemo(
    () => ({
      a: ({ children, ...props }: { children?: React.ReactNode }) => (
        <a className="text-brandSecondaryBlue hover:underline" {...props}>
          {children}
        </a>
      ),
      p: ({ children }: { children?: React.ReactNode }) => <p className="[&:not(:first-child)]:my-2">{children}</p>,
      em: ({ children }: { children?: React.ReactNode }) => <em>{children}</em>,
      ol: ({ children, ...props }: { children?: React.ReactNode }) => (
        <ol className="list-decimal pl-4 space-y-2 my-3" {...props}>
          {children}
        </ol>
      ),
      ul: ({ children, ...props }: { children?: React.ReactNode }) => (
        <ul className="list-disc pl-4 space-y-2 my-3" {...props}>
          {children}
        </ul>
      ),
      blockquote: ({ children }: { children?: React.ReactNode }) => (
        <blockquote className="border-l-4 border-[#f2f2f2] pl-4 py-2 [&>p]:mb-2">{children}</blockquote>
      ),
      hr: () => <hr className="my-4 border-neutral-200" />,
      code: ({ children }: { children?: React.ReactNode }) => {
        if (!children) return null;
        const text = Array.isArray(children) ? children.join('') : String(children);

        if (text.startsWith('prompt:')) {
          const prompt = text.replace('prompt:', '').trim();
          return (
            <PromptButton
              prompt={prompt}
              isLastMessage={isLastMessage}
              promptSelected={promptSelected}
              onPromptClick={onPromptClick}
            />
          );
        }
        return <code>{children}</code>;
      }
    }),
    [isLastMessage, promptSelected, onPromptClick]
  );

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {cleanContent}
    </ReactMarkdown>
  );
};

export const ChatMessage = ({ role, content, isLastMessage }: ChatMessageProps) => {
  const { onSendMessage } = useChat();
  const [promptSelected, setPromptSelected] = useState<string | null>(null);

  const isAssistant = role === MessageRole.ASSISTANT;

  const handlePromptClick = (prompt: string) => {
    onSendMessage(prompt);
    setPromptSelected(prompt);
  };

  const segments = useMemo(
    () =>
      content
        .split(/(<copyToClipboard>[\s\S]*?<\/copyToClipboard>)/g)
        .map(text => {
          const match = text.match(/<copyToClipboard>([\s\S]*?)<\/copyToClipboard>/);
          return {
            text: match ? match[1] : text,
            isTagged: !!match
          };
        })
        .filter(segment => segment.text),
    [content]
  );

  return (
    <div className={cn('flex justify-end gap-5 m-5', { 'justify-start': isAssistant })}>
      {isAssistant && <AssistantAvatar />}
      <div
        className={cn('max-w-[80%] text-left leading-6', {
          'bg-[#f8f8f8] text-gray-800 p-4 rounded-xl': !isAssistant
        })}
      >
        {segments.map((segment, index) => {
          const messageContent = (
            <MessageRenderer
              content={segment.text}
              isLastMessage={isLastMessage}
              promptSelected={promptSelected}
              onPromptClick={handlePromptClick}
            />
          );

          if (segment.isTagged) {
            return (
              <CopyableBlock key={index} text={segment.text}>
                {messageContent}
              </CopyableBlock>
            );
          }

          return <div key={index}>{messageContent}</div>;
        })}
      </div>
    </div>
  );
};
